import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  Output,
} from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';

import { type DatasetColumn, DatasetColumnFormat } from '@blaze-shared/schema-domain';

import { ExtraStartAction } from '../blaze-table/blaze-table.model';
import { STANDALONE_FIELD_INPUT_PIPES } from '../field-input/field-input.pipe';
import { BlazeTableLoggerService } from '../shared/blaze-table-logger.service';
import { type BlazeTableValue } from '../shared/blaze-table.service';

import { CELL_INPUT_TYPES_COMPONENTS } from './cell-input-types';

interface ColumnDataFormatMapToCellInputType {
  avoidDataTypes?: string[];
  columnType?: string;
  dataType?: string;
  formatTypes?: DatasetColumnFormat['formatType'][];
  isAccessRole?: boolean;
  isMissingFormat?: boolean;
  isProfilePicture?: boolean;
  isSecure?: boolean;
}

type CellInputType =
  | 'Boolean'
  | 'Text'
  | 'DateTime'
  | 'Number'
  | 'Secure'
  | 'Paragraph'
  | 'File'
  | 'Link'
  | 'Tag'
  | 'Signature'
  | 'GeoPoint'
  | 'TimeZone'
  | 'Duration'
  | 'PhoneNumber'
  | 'Formula';

const COLUMN_TO_CELL_INPUT_MAP: Record<CellInputType, ColumnDataFormatMapToCellInputType[]> = {
  Boolean: [{ columnType: 'Boolean' }],
  DateTime: [
    { columnType: 'DateTime' },
    { columnType: 'Date' },
    { columnType: 'Time' },
    { columnType: 'CreatedTime' },
    { columnType: 'LastUpdatedTime' },
  ],
  Formula: [{ columnType: 'Formula', avoidDataTypes: ['Number', 'File', 'Image', 'Duration'] }],
  Duration: [{ formatTypes: ['Duration'] }],
  File: [{ columnType: 'File' }, { columnType: 'Image' }],
  GeoPoint: [{ columnType: 'GeoPoint' }],
  Link: [{ columnType: 'Link' }],
  Number: [
    { columnType: 'Number' },
    { columnType: 'AutoNumber' },
    { columnType: 'Formula', dataType: 'Number' },
  ],
  Paragraph: [{ columnType: 'String', formatTypes: ['Paragraph'] }],
  PhoneNumber: [{ formatTypes: ['PhoneNumber'] }],
  Secure: [{ dataType: 'String', isSecure: true }],
  Signature: [{ columnType: 'Signature' }],
  Tag: [{ columnType: 'Tag' }, { dataType: 'Tag', columnType: 'AccessRole', isAccessRole: true }],
  Text: [
    { dataType: 'Object' },
    {
      columnType: 'String',
      isSecure: false,
      formatTypes: ['SingleLine', 'ZipCode', 'RichText', 'Email', 'Url'],
    },
    { columnType: 'String', isSecure: false, isMissingFormat: true },
    { columnType: 'UpdatedBy' },
    { columnType: 'CreatedBy' },
  ],
  TimeZone: [{ formatTypes: ['TimeZone'] }],
};

@UntilDestroy()
@Component({
  selector: 'blaze-cell-input',
  templateUrl: './cell-input.component.html',
  styleUrls: ['./cell-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [...STANDALONE_FIELD_INPUT_PIPES, ...CELL_INPUT_TYPES_COMPONENTS],
})
export class CellInputComponent {
  private readonly logger = inject(BlazeTableLoggerService);
  elementRef = inject(ElementRef);

  @Input() error = '';
  @Input() isDisabled = false;
  @Input() rowNum!: number;
  @Input() rowSelected = false;
  @Input() value!: BlazeTableValue;
  @Input() columns: DatasetColumn[] = [];
  @Input() placeholder = '';
  @Input() projectId: string;
  @Input() isNewRow = false;
  @Input() forceFocusAfterInit = false;
  @Input() extraStartAction: ExtraStartAction | undefined;
  @Input() parentTableCell?: HTMLElement;
  @Input() isFirst = false;
  @Input() selectedText?: string;
  @Input() selectedTextOffset?: number;

  @Output() valueChanged = new EventEmitter<BlazeTableValue>();
  @Output() focusLost = new EventEmitter<BlazeTableValue>();

  private _column!: DatasetColumn;

  cellInputType?: CellInputType;

  @Input() set column(value: DatasetColumn) {
    this._column = value;
    this.cellInputType = this.getColumnInputType(value);
  }
  get column(): DatasetColumn {
    return this._column;
  }

  @HostBinding('class.active-border') get activeBorder(): boolean {
    return !!this.parentTableCell;
  }
  @HostBinding('class.first-col') get includeFirstColPadding(): boolean {
    return this.isFirst;
  }
  @HostBinding('class.selected') get isSelected(): boolean {
    return this.rowSelected;
  }

  emitValueChanged(value: BlazeTableValue): void {
    this.logger.log('valueChanged emitted:', value);
    this.valueChanged.emit(value);
  }

  emitFocusLost(value: BlazeTableValue): void {
    this.logger.log('focusLost emitted:', value);
    this.focusLost.emit(value);
  }

  getColumnInputType(column: DatasetColumn): CellInputType | undefined {
    return Object.entries(COLUMN_TO_CELL_INPUT_MAP).find(([, rules]) =>
      rules.find((rule) =>
        Object.entries(rule)
          .map(([key, value]) => {
            if (key === 'dataType') {
              return column.dataType === value;
            } else if (key === 'columnType') {
              return column.columnType === value;
            } else if (key === 'formatTypes') {
              return !!(column.format?.formatType && value.includes(column.format.formatType));
            } else if (key === 'isMissingFormat') {
              return !column?.format?.formatType;
            } else if (key === 'isSecure') {
              return column.isSecure === value;
            } else if (key === 'isAccessRole') {
              return column.isAccessRole === value;
            } else if (
              key === 'avoidDataTypes' &&
              Array.isArray(value) &&
              typeof column.dataType === 'string'
            ) {
              return !value.includes(column.dataType);
            }
            return undefined;
          })
          .every((result) => !!result),
      ),
    )?.[0] as CellInputType | undefined;
  }
}
